import React, { useState, useEffect, useRef } from 'react';
import { ReactComponent as BggLogo } from '../powered-by-bgg-rgb.svg';
import { Link, useLocation } from 'react-router-dom';
import { CgMenuGridR } from "react-icons/cg";
import { AiOutlineInfoCircle, AiOutlineEye, AiOutlineThunderbolt } from "react-icons/ai";
import { RiStackFill, RiStackLine } from "react-icons/ri";
import { BsSun, BsMoon } from "react-icons/bs";
import { GiMasonJar } from "react-icons/gi";
import SlidePanel from './slide-panel.js';
import useGlobalState from '../custom-hooks/use-global-state.js';

const Header = () => {
  const { isDarkMode, toggleDarkMode } = useGlobalState();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);
  const [isTipJarExpanded, setIsTipJarExpanded] = useState(false);
  const location = useLocation();

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    
    switch(path) {
      case '/':
        return 'Shop';
      case '/play':
        return 'Play';
      case '/research':
        return 'Research';
      case '/sell':
        return 'Sell';
      case '/trade':
        return 'Trade';
      default:
        return 'Honor Mountain';
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleClickOutside = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setIsMenuOpen(false);
    }
  };

  const toggleAbout = () => {
    setIsAboutExpanded(!isAboutExpanded);
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div>
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <div className="icon-menu" ref={menuRef}>
              <button className="icon-menu-button" onClick={toggleMenu}>
                <CgMenuGridR />
              </button>
              <div className={`icon-menu-panel ${isMenuOpen ? 'open' : ''}`}>
                <Link to="/" className="icon-menu-item" onClick={toggleMenu}>Shop</Link>
                {/* <Link to="/play" className="icon-menu-item" onClick={toggleMenu}>Play</Link> */}
                <Link to="/research" className="icon-menu-item" onClick={toggleMenu}>Research</Link>
                <Link to="/sell" className="icon-menu-item" onClick={toggleMenu}>Sell</Link>
                {/* <Link to="/trade" className="icon-menu-item" onClick={toggleMenu}>Trade</Link> */}
              </div>
            </div>
            <h1 className="app-title">{getPageTitle()}</h1>
          </div>
          <div className="header-right">
            <button 
              className="icon-menu-button dark-mode-toggle" 
              onClick={toggleDarkMode}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <BsSun /> : <BsMoon />}
            </button>
            <button 
              className="icon-menu-button tip-jar-button" 
              onClick={() => setIsTipJarExpanded(true)}
              title="Tips Appreciated"
            >
              <GiMasonJar />
            </button>
            <button className="icon-menu-button info-button" onClick={toggleAbout}>
              <AiOutlineInfoCircle />
            </button>
          </div>
        </div>
      </header>

      {/* About slide-in panel */}
      <SlidePanel
        isOpen={isAboutExpanded}
        onClose={toggleAbout}
        title="About"
        width="380px"
      >
        {/* About Research */}
        {location.pathname === '/research' && (
          <div className="about-details-panel">
            <div>
              <h3 className="about-title">Instructions</h3>
              <ol className="about-list">
                <li>Click the input field.</li>
                <li>Type the name of a game you'd want information on</li>
                <li>Click the price history button</li>
              </ol>
            </div>
          </div>
        )}

        {/* About Sell */}
        {location.pathname === '/sell' && (
          <div style={{ marginTop: '10px', textAlign: 'left', padding: '10px', lineHeight: '1.6' }}>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                  <li><strong>Load Collection:</strong> Enter your BGG username to load your owned games</li>
                  <li><strong>Set Price Percentage:</strong> Adjust the percentage of average market price (default 80%)</li>
                  <li><strong>Load Market Prices:</strong> Click to fetch average prices from BGG marketplace</li>
                  <li><strong>Customize:</strong> Edit version names, prices, and add notes for each game</li>
                  <li><strong>Enter VFM ID:</strong> Enter the Geeklist ID for your Virtual Flea Market (found in the URL)</li>
                  <li><strong>Generate Links:</strong> Click "Generate VFM Links" to create add links for each item</li>
                  <li><strong>List Items:</strong> Click "Add to VFM" links to add items to the VFM on BGG</li>
              </ol>
          </div>
        )}

        {/* About Shop */}
        {location.pathname === '/' && (
          <div className="about-details-panel">
            <div>
              <h3 className="about-title">Instructions</h3>
              <ol className="about-list">
                <li>Enter a geeklist id in the search box and click 'Load'. Or click the input field for a list or recent geeklist auctions to select from.</li>
                <ul>
                  <li>Large lists will take a minute or two to load.</li>
                </ul>
                <li>Use the table headers to sort and/filter the listings.</li>
                <ul>
                  <li>Clicking a column header with change the sort for that column.</li>
                  <li>Clicking the icon (hamburger icon) button at the right side of the column will allow you to filter.</li>
                  <li>Clicking / tapping and holding will allow you to drag and drop the order of the columns.</li>
                </ul>
              </ol>
              <h3 className="about-title">Other Tips</h3>
              <ul className="about-list">
                <li>When sorting, hold shift while clicking subsequent column headers for multi-sort</li>
                <li>If you click the <b>Summary</b> button in the <b>Listing Info</b> column it will load a modal of the listing description and comments so you can quickly see if it's sold or what the current bid is.</li>
                <li>The <b>Listing Info</b> modal also houses the average price of the item based on recent sales data from BGG</li>
                <li>Clicking the sellers name in the <b>Seller</b> column will filter the view to only those sellers items.</li>
                <li>The page takes two url parameters. 'geeklistId' is the id of the geeklist to auto load. 'seller' is the username of the seller to filter to automatically when the data is loaded. For example /?geeklistId=355629&seller=branhammer will load the geeklist for the 2025 BGG Spring VFM filtered to Branhammers listings.</li>
                <li>The geeklist items load filtered to those items that are not sold. Sold items can be viewed by adjusting the filter or by clicking the <b>Show Sold</b> button in the page controls.</li>
              </ul>
              <h3 className="about-title"><AiOutlineEye /> Advanced View Features</h3>
              <ul className='about-list'>
                <li>The <b>Average Price</b> column has a button that will lookup the price history of the game and report the average price and what percentage difference there is between the average sale price and the list price. If the listing includes linked extras, A filled stack icon (<RiStackFill />) will display if all extras linked in the body are included in the average price calculation. An empty stacked icon (<RiStackLine />) will display if sale history prices were not found for linked extras.</li>
                <li>If the list is filtered to be less than 100 items then the <b>Bulk Lookup Prices</b> (<AiOutlineThunderbolt />) button can be used to lookup the average price of all the items in the filtered view.</li>
                <li>The <b>Matches</b> column displays how many other listings are for the same game in the current filtered view. Clicking the button will filter the view to only those games.</li>
                <li>The checkboxes in each row can be used to track a particular item across different filter views. Clicking the checkbox in the header will filter the view to those that have been selected. The ids for those items are also added to the url, so you can copy the url and paste it back in later to get to your selection.</li>
                <li>Entering a zip code in the controls and clicking apply will allow the <b>Location</b> column to be sorted by distance from the zip code.</li>
              </ul>
            </div>

            <div>
              <h3 className="about-title">Known Issues</h3>
              <ul className="about-list">
                <li>Some items are actually sold but render here as not sold. It depends on if the word 'sold' appears in the description or if the seller has marked the item as sold. Some don't use the 'Mark As Sold' button, so the next best thing is to use the <b>Summary</b> button in the <b>Listing Info</b> column to view the listing body and comment history.</li>
                <li>Some items render with the wrong price listed. It depends on the content of the description. I parse the description first for a price with an auction type (SB, FP, BIN...), I call that a structured price. Then I parse for an unstructured price with a currency symbol ($, £, €). Finally I parse for the first number in the body and assume it's the price. Unfortunately, people who offer deals part of their listing sometimes make it difficult to have a generic function identify item price. For instance, "Buy one of my games and get any other for $5 off" appearing above the listed price (without an auction type) would cause my function to think it's a $5 item.</li>
              </ul>
            </div>
          </div>
        )}

        <div className="about-footer">
          <div>
            Created by Steven Branham (<a href="https://boardgamegeek.com/user/branhammer" className="author-link">Branhammer</a>)
          </div>
          <BggLogo className="bgg-logo" />
        </div>
      </SlidePanel>

      {/* Tip Jar slide-in panel */}
      <SlidePanel
        isOpen={isTipJarExpanded}
        onClose={() => setIsTipJarExpanded(false)}
        title="Tips are Appreciated"
        width="340px"
      >
        <div className="tip-jar-content">
          <p className="tip-jar-description">
            If you find this tool valuable, consider leaving a tip! I'll work on the tool either way, but your support is extra motivation!
          </p>
          
          <div className="tip-jar-options">
            <a 
              href="https://paypal.me/branhammer" 
              target="_blank" 
              rel="noopener noreferrer"
              className="tip-jar-button-link paypal"
            >
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.194a.55.55 0 0 0-.544.465l-.652 4.126a.55.55 0 0 1-.544.465H7.076v-.05z"/>
              </svg>
              Tip with PayPal
            </a>
          </div>
          
          <p className="tip-jar-note">
            <small>All contributions are appreciated, no matter the size!</small>
          </p>
        </div>
      </SlidePanel>
    </div>
  );
};

export default Header;